import React, { useState, useEffect } from 'react'
import { 
  Save,
  RotateCcw,
  Phone,
  User,
  MessageSquare,
  Target,
  CheckCircle,
  ChevronRight,
  HelpCircle
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import autoIncrementService from '../../services/autoIncrementService'
import { callsService } from '../../services/callsService'

const CallsForm = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = Boolean(id)
  const isView = window.location.pathname.includes('/view/')
  
  const [activeTab, setActiveTab] = useState('callInfo')
  const [showHelp, setShowHelp] = useState(false)
  const [isLoadingCallCode, setIsLoadingCallCode] = useState(false)
  const [loading, setLoading] = useState(false)
  
  const [formData, setFormData] = useState({
    // Call Information
    callCode: '',
    callDate: new Date().toISOString().split('T')[0],
    callTime: '',
    callType: '',
    callStatus: '',
    priority: '',
    callMethod: '',
    callPlatform: '',
    callDuration: '',
    // Caller Information
    callerName: '',
    callerEmail: '',
    callerPhone: '',
    callerCompany: '',
    callerPosition: '',
    // Call Details
    callSubject: '',
    callPurpose: '',
    callDescription: '',
    callRecording: '',
    // Related Records
    lead: '',
    prospect: '',
    customer: '',
    opportunity: '',
    // Call Outcome
    callOutcome: '',
    outcomeDescription: '',
    nextSteps: '',
    followUpDate: '',
    followUpNotes: ''
  })

  // Master data for dropdowns
  const [leads, setLeads] = useState([])
  const [prospects, setProspects] = useState([])
  const [customers, setCustomers] = useState([])

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Home', path: '/dashboard' },
    { label: 'CRM', path: '/crm' },
    { label: 'Calls', path: '/crm/calls' },
    { label: isEdit ? (isView ? 'View Call' : 'Edit Call') : 'New Call' }
  ]

  // Load master data and call code on component mount
  useEffect(() => {
    if (!isView) {
        loadCallCode()
      }
    loadMasterData()
    if (isEdit) {
    loadCallData()
    }
  }, [id, isEdit, isView])

  const loadCallCode = async () => {
    try {
      setIsLoadingCallCode(true)
      const response = await autoIncrementService.getCurrentValue('CALL')
      if (response.success) {
        const sequence = response.data.formattedValue
        setFormData(prev => ({
          ...prev,
          callCode: sequence
        }))
      } else {
        setFormData(prev => ({
          ...prev,
          callCode: 'CALL-0001'
        }))
        console.warn('Auto-increment sequence CALL not found, using placeholder')
      }
    } catch (error) {
      console.error('Error loading call code:', error)
      setFormData(prev => ({
        ...prev,
        callCode: 'CALL-0001'
      }))
      console.warn('Using placeholder call code due to auto-increment error')
    } finally {
      setIsLoadingCallCode(false)
    }
  }

    const loadMasterData = async () => {
      try {
      // Load leads, prospects, customers, etc.
      // This would typically load from respective services
              setLeads([])
              setProspects([])
              setCustomers([])
    } catch (error) {
      console.error('Error loading master data:', error)
      toast.error('Failed to load master data')
    }
  }

  const loadCallData = async () => {
    if (!id) return
    
    try {
      setLoading(true)
      const response = await callsService.getCallById(id)
            if (response.success) {
        setFormData(response.data)
            } else {
        toast.error(response.message || 'Failed to load call data')
        navigate('/crm/calls')
      }
      } catch (error) {
      console.error('Error loading call data:', error)
      toast.error('Failed to load call data')
      navigate('/crm/calls')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      setLoading(true)
      const callData = {
        ...formData
      }
      
      let response
      if (isEdit) {
        response = await callsService.updateCall(id, callData)
        } else {
        response = await callsService.createCall(callData)
        }

        if (response.success) {
        toast.success(isEdit ? 'Call updated successfully' : 'Call created successfully')
          navigate('/crm/calls')
        } else {
        toast.error(response.message || `Failed to ${isEdit ? 'update' : 'create'} call`)
      }
    } catch (error) {
      console.error(`Error ${isEdit ? 'updating' : 'creating'} call:`, error)
      toast.error(`Failed to ${isEdit ? 'update' : 'create'} call`)
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setFormData({
      callCode: '',
      callDate: new Date().toISOString().split('T')[0],
      callTime: '',
      callType: '',
      callStatus: '',
      priority: '',
      callMethod: '',
      callPlatform: '',
      callDuration: '',
      callerName: '',
      callerEmail: '',
      callerPhone: '',
      callerCompany: '',
      callerPosition: '',
      callSubject: '',
      callPurpose: '',
      callDescription: '',
      callRecording: '',
      lead: '',
      prospect: '',
      customer: '',
      opportunity: '',
      callOutcome: '',
      outcomeDescription: '',
      nextSteps: '',
      followUpDate: '',
      followUpNotes: ''
    })
    if (!isView) {
      loadCallCode()
    }
  }

  if (loading) {
    return (
      <div className="sales-dashboard">
        <div className="page-header">
          <div className="page-title">
            <h1>Loading...</h1>
            <p>Please wait while we load the call form</p>
          </div>
        </div>
        <div className="form-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    )
  }

  const helpContent = (
    <>
      <h3>What is Call Master?</h3>
      <p>Call Master lets you <strong>create and track call records</strong>—outbound/inbound calls linked to leads, prospects, or customers, with caller details, subject, outcome, and follow-up.</p>
      <h3>Tabs</h3>
      <p><strong>Call Information</strong> – Call code, date, time, type, status, method, platform, duration.</p>
      <p><strong>Caller Details</strong> – Caller name, email, phone, company, position.</p>
      <p><strong>Call Details</strong> – Subject, purpose, description, recording.</p>
      <p><strong>Related Records</strong> – Link to lead, prospect, customer, opportunity.</p>
      <p><strong>Call Outcome</strong> – Outcome, description, next steps, follow-up date and notes.</p>
      <h3>How to use</h3>
      <p>Fill required fields (e.g. call date, time, type). Use <strong>Save</strong> to create. Use <strong>Reset</strong> to clear or navigate back to cancel.</p>
    </>
  )

  return (
    <div className="sales-dashboard">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />

      <div className="page-header">
        <div className="page-header-content">
        <div className="page-title">
          <h1>{isEdit ? 'Edit Call' : isView ? 'View Call' : 'New Call'}</h1>
          <p>{isEdit ? 'Update call information and details' : isView ? 'View call information and details' : 'Create a new call record and track conversation'}</p>
        </div>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
            <h2>Call Master Entry</h2>
          </div>

          {/* Tab Navigation */}
      <div className="tab-navigation">
            <button
              className={`tab-button ${activeTab === 'callInfo' ? 'active' : ''}`}
              onClick={() => setActiveTab('callInfo')}
            >
              <Phone size={18} />
          <span>Call Information</span>
            </button>
            <button
              className={`tab-button ${activeTab === 'callerInfo' ? 'active' : ''}`}
              onClick={() => setActiveTab('callerInfo')}
            >
              <User size={18} />
          <span>Caller Details</span>
            </button>
            <button
              className={`tab-button ${activeTab === 'callDetails' ? 'active' : ''}`}
              onClick={() => setActiveTab('callDetails')}
            >
              <MessageSquare size={18} />
          <span>Call Details</span>
            </button>
            <button
              className={`tab-button ${activeTab === 'relatedRecords' ? 'active' : ''}`}
              onClick={() => setActiveTab('relatedRecords')}
            >
              <Target size={18} />
          <span>Related Records</span>
            </button>
            <button
              className={`tab-button ${activeTab === 'callOutcome' ? 'active' : ''}`}
              onClick={() => setActiveTab('callOutcome')}
            >
              <CheckCircle size={18} />
          <span>Call Outcome</span>
            </button>
          </div>

      <form className="form-content" onSubmit={handleSubmit}>

        {/* Call Information Tab */}
            {activeTab === 'callInfo' && (
              <div className="tab-content">
            <div className="section-header">
                  <h3>Call Information</h3>
                </div>
                
            {/* Row 1: Call Code, Date, Time, Type */}
            <div className="grid-row-4">
                    <div className="form-group">
                <label className="form-label required">Call Code</label>
                      <input
                        type="text"
                  value={isLoadingCallCode ? 'Generating...' : formData.callCode}
                  readOnly
                        className="form-input"
                        placeholder="Auto-generated"
                  style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
                      />
                    </div>
                    <div className="form-group">
                <label className="form-label required">Call Date</label>
                      <input
                        type="date"
                        value={formData.callDate}
                        onChange={(e) => handleInputChange('callDate', e.target.value)}
                        className="form-input"
                        required
                      />
                    </div>
                    <div className="form-group">
                <label className="form-label required">Call Time</label>
                      <input
                        type="time"
                        value={formData.callTime}
                        onChange={(e) => handleInputChange('callTime', e.target.value)}
                        className="form-input"
                        required
                      />
                    </div>
                    <div className="form-group">
                <label className="form-label required">Call Type</label>
                      <select
                        value={formData.callType}
                        onChange={(e) => handleInputChange('callType', e.target.value)}
                  className="form-input"
                        required
                      >
                        <option value="">Select Call Type</option>
                        <option value="Outbound">Outbound</option>
                        <option value="Inbound">Inbound</option>
                        <option value="Follow-up">Follow-up</option>
                        <option value="Support">Support</option>
                        <option value="Sales">Sales</option>
                        <option value="Marketing">Marketing</option>
                      </select>
                    </div>
                  </div>

            {/* Row 2: Call Status, Priority, Call Method */}
            <div className="grid-row-3">
                    <div className="form-group">
                <label className="form-label required">Call Status</label>
                      <select
                        value={formData.callStatus}
                        onChange={(e) => handleInputChange('callStatus', e.target.value)}
                  className="form-input"
                        required
                      >
                        <option value="">Select Status</option>
                        <option value="Scheduled">Scheduled</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Completed">Completed</option>
                        <option value="Missed">Missed</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    </div>
                    <div className="form-group">
                <label className="form-label required">Priority</label>
                      <select
                        value={formData.priority}
                        onChange={(e) => handleInputChange('priority', e.target.value)}
                  className="form-input"
                        required
                      >
                        <option value="">Select Priority</option>
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Call Method</label>
                      <select
                        value={formData.callMethod}
                        onChange={(e) => handleInputChange('callMethod', e.target.value)}
                  className="form-input"
                      >
                        <option value="">Select Method</option>
                        <option value="Phone Call">Phone Call</option>
                        <option value="Video Call">Video Call</option>
                        <option value="Conference Call">Conference Call</option>
                        <option value="VoIP">VoIP</option>
                        <option value="Mobile">Mobile</option>
                      </select>
                    </div>
            </div>

            {/* Row 3: Call Platform, Duration */}
            <div className="grid-row-2">
                    <div className="form-group">
                      <label className="form-label">Call Platform</label>
                      <select
                        value={formData.callPlatform}
                        onChange={(e) => handleInputChange('callPlatform', e.target.value)}
                  className="form-input"
                      >
                        <option value="">Select Platform</option>
                        <option value="Zoom">Zoom</option>
                  <option value="Teams">Microsoft Teams</option>
                  <option value="Google Meet">Google Meet</option>
                  <option value="Skype">Skype</option>
                  <option value="Phone">Phone</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Call Duration (minutes)</label>
                      <input
                        type="number"
                        value={formData.callDuration}
                  onChange={(e) => handleInputChange('callDuration', parseInt(e.target.value) || 0)}
                        className="form-input"
                  placeholder="Enter duration in minutes"
                  min="0"
                />
                  </div>
                </div>
              </div>
            )}

        {/* Caller Information Tab */}
            {activeTab === 'callerInfo' && (
              <div className="tab-content">
            <div className="section-header">
                  <h3>Caller Information</h3>
                </div>
                
            {/* Row 1: Caller Name, Email, Phone */}
            <div className="grid-row-3">
                    <div className="form-group">
                <label className="form-label required">Caller Name</label>
                      <input
                        type="text"
                        value={formData.callerName}
                        onChange={(e) => handleInputChange('callerName', e.target.value)}
                        className="form-input"
                        placeholder="Enter caller name"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Caller Email</label>
                      <input
                        type="email"
                        value={formData.callerEmail}
                        onChange={(e) => handleInputChange('callerEmail', e.target.value)}
                        className="form-input"
                  placeholder="Enter caller email"
                      />
                    </div>
                    <div className="form-group">
                <label className="form-label">Caller Phone</label>
                      <input
                  type="tel"
                  value={formData.callerPhone}
                  onChange={(e) => handleInputChange('callerPhone', e.target.value)}
                        className="form-input"
                  placeholder="Enter caller phone"
                      />
                    </div>
                  </div>

            {/* Row 2: Company, Position */}
            <div className="grid-row-2">
                    <div className="form-group">
                <label className="form-label">Caller Company</label>
                      <input
                        type="text"
                  value={formData.callerCompany}
                  onChange={(e) => handleInputChange('callerCompany', e.target.value)}
                        className="form-input"
                  placeholder="Enter caller company"
                      />
                    </div>
                    <div className="form-group">
                <label className="form-label">Caller Position</label>
                      <input
                        type="text"
                  value={formData.callerPosition}
                  onChange={(e) => handleInputChange('callerPosition', e.target.value)}
                        className="form-input"
                  placeholder="Enter caller position"
                      />
                    </div>
                </div>
              </div>
            )}

        {/* Call Details Tab */}
            {activeTab === 'callDetails' && (
              <div className="tab-content">
            <div className="section-header">
                  <h3>Call Details</h3>
                </div>
                
            {/* Row 1: Subject, Purpose */}
            <div className="grid-row-2">
                    <div className="form-group">
                <label className="form-label required">Call Subject</label>
                      <input
                        type="text"
                  value={formData.callSubject}
                  onChange={(e) => handleInputChange('callSubject', e.target.value)}
                        className="form-input"
                  placeholder="Enter call subject"
                  required
                      />
                    </div>
                    <div className="form-group">
                <label className="form-label">Call Purpose</label>
                <select
                  value={formData.callPurpose}
                  onChange={(e) => handleInputChange('callPurpose', e.target.value)}
                        className="form-input"
                >
                  <option value="">Select Purpose</option>
                  <option value="Sales">Sales</option>
                  <option value="Support">Support</option>
                  <option value="Follow-up">Follow-up</option>
                  <option value="Information">Information</option>
                  <option value="Demo">Demo</option>
                  <option value="Negotiation">Negotiation</option>
                </select>
                    </div>
                  </div>

            {/* Row 2: Description */}
            <div className="grid-row-1">
              <div className="form-group">
                <label className="form-label">Call Description</label>
                      <textarea
                        value={formData.callDescription}
                        onChange={(e) => handleInputChange('callDescription', e.target.value)}
                  className="form-input"
                  rows="4"
                        placeholder="Enter detailed call description"
                      />
                    </div>
                  </div>

            {/* Row 3: Recording */}
            <div className="grid-row-1">
              <div className="form-group">
                <label className="form-label">Call Recording</label>
                <input
                  type="text"
                  value={formData.callRecording}
                  onChange={(e) => handleInputChange('callRecording', e.target.value)}
                  className="form-input"
                  placeholder="Enter recording URL or reference"
                />
                  </div>
                </div>
              </div>
            )}

        {/* Related Records Tab */}
            {activeTab === 'relatedRecords' && (
              <div className="tab-content">
            <div className="section-header">
                  <h3>Related Records</h3>
                </div>
                
            {/* Row 1: Lead, Prospect */}
            <div className="grid-row-2">
                    <div className="form-group">
                      <label className="form-label">Lead</label>
                      <select
                        value={formData.lead}
                        onChange={(e) => handleInputChange('lead', e.target.value)}
                  className="form-input"
                      >
                        <option value="">Select Lead</option>
                  {leads.map(lead => (
                          <option key={lead._id} value={lead._id}>
                            {lead.leadCode} - {lead.prospectName}
                          </option>
                  ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Prospect</label>
                      <select
                        value={formData.prospect}
                        onChange={(e) => handleInputChange('prospect', e.target.value)}
                  className="form-input"
                      >
                        <option value="">Select Prospect</option>
                  {prospects.map(prospect => (
                          <option key={prospect._id} value={prospect._id}>
                            {prospect.prospectCode} - {prospect.prospectName}
                          </option>
                  ))}
                      </select>
                    </div>
            </div>

            {/* Row 2: Customer, Opportunity */}
            <div className="grid-row-2">
                    <div className="form-group">
                      <label className="form-label">Customer</label>
                      <select
                        value={formData.customer}
                        onChange={(e) => handleInputChange('customer', e.target.value)}
                  className="form-input"
                      >
                        <option value="">Select Customer</option>
                  {customers.map(customer => (
                          <option key={customer._id} value={customer._id}>
                            {customer.customerCode} - {customer.customerName}
                          </option>
                  ))}
                      </select>
                    </div>
                    <div className="form-group">
                <label className="form-label">Opportunity</label>
                      <input
                        type="text"
                  value={formData.opportunity}
                  onChange={(e) => handleInputChange('opportunity', e.target.value)}
                        className="form-input"
                  placeholder="Enter opportunity reference"
                      />
                  </div>
                </div>
              </div>
            )}

        {/* Call Outcome Tab */}
            {activeTab === 'callOutcome' && (
              <div className="tab-content">
            <div className="section-header">
                  <h3>Call Outcome</h3>
                </div>
                
            {/* Row 1: Outcome */}
            <div className="grid-row-1">
                    <div className="form-group">
                      <label className="form-label">Call Outcome</label>
                      <select
                        value={formData.callOutcome}
                        onChange={(e) => handleInputChange('callOutcome', e.target.value)}
                  className="form-input"
                      >
                  <option value="">Select Outcome</option>
                        <option value="Successful">Successful</option>
                  <option value="Partially Successful">Partially Successful</option>
                  <option value="Unsuccessful">Unsuccessful</option>
                        <option value="Follow-up Required">Follow-up Required</option>
                        <option value="No Answer">No Answer</option>
                        <option value="Busy">Busy</option>
                      </select>
                    </div>
            </div>

            {/* Row 2: Outcome Description */}
            <div className="grid-row-1">
                    <div className="form-group">
                <label className="form-label">Outcome Description</label>
                <textarea
                  value={formData.outcomeDescription}
                  onChange={(e) => handleInputChange('outcomeDescription', e.target.value)}
                  className="form-input"
                  rows="3"
                  placeholder="Describe the call outcome and results"
                />
                    </div>
            </div>

            {/* Row 3: Next Steps */}
            <div className="grid-row-1">
              <div className="form-group">
                <label className="form-label">Next Steps</label>
                <textarea
                  value={formData.nextSteps}
                  onChange={(e) => handleInputChange('nextSteps', e.target.value)}
                  className="form-input"
                  rows="3"
                  placeholder="Enter next steps and action items"
                />
              </div>
            </div>

            {/* Row 4: Follow-up Date, Notes */}
            <div className="grid-row-2">
                    <div className="form-group">
                      <label className="form-label">Follow-up Date</label>
                      <input
                        type="date"
                        value={formData.followUpDate}
                        onChange={(e) => handleInputChange('followUpDate', e.target.value)}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Follow-up Notes</label>
                      <input
                        type="text"
                        value={formData.followUpNotes}
                        onChange={(e) => handleInputChange('followUpNotes', e.target.value)}
                        className="form-input"
                        placeholder="Enter follow-up notes"
                      />
                  </div>
                </div>
              </div>
            )}

      {/* Form Actions */}
      <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleReset}
          >
            <RotateCcw size={18} />
          Reset
        </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
          >
            <Save size={18} />
            {loading ? 'Saving...' : (isEdit ? 'Update Call' : 'Create Call')}
        </button>
        </div>
      </form>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Call Master Entry – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default CallsForm